import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { Gift, Sparkles, Filter, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import BonusCard from "../components/BonusCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const bonusTypes = [
  { value: "welcome", label: "Welcome Bonus" },
  { value: "no-deposit", label: "No Deposit" },
  { value: "free-spins", label: "Free Spins" },
  { value: "reload", label: "Reload Bonus" },
];

export default function Bonuses() {
  const [bonuses, setBonuses] = useState([]);
  const [filteredBonuses, setFilteredBonuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bonusType, setBonusType] = useState(null);
  const [exclusiveOnly, setExclusiveOnly] = useState(false);

  useEffect(() => {
    const fetchBonuses = async () => {
      try {
        const response = await axios.get(`${API}/bonuses`);
        setBonuses(response.data);
        setFilteredBonuses(response.data);
      } catch (error) {
        console.error("Error fetching bonuses:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchBonuses();
  }, []);

  useEffect(() => {
    let result = bonuses;

    if (bonusType) {
      result = result.filter(b => b.bonus_type === bonusType);
    }

    if (exclusiveOnly) {
      result = result.filter(b => b.is_exclusive);
    }

    setFilteredBonuses(result);
  }, [bonusType, exclusiveOnly, bonuses]);

  const clearFilters = () => {
    setBonusType(null);
    setExclusiveOnly(false);
  };

  return (
    <>
      <Helmet>
        <title>Best Casino Bonuses & Free Spins 2026 – PlaySpiro</title>
        <meta name="description" content="Claim exclusive casino bonuses, free spins and no deposit offers. Updated daily. Find the best bonus deals for slots, crash games and live casinos at PlaySpiro." />
        <link rel="canonical" href="https://playspiro.com/bonuses" />
        <meta property="og:title" content="Best Casino Bonuses & Free Spins 2026 – PlaySpiro" />
        <meta property="og:description" content="Exclusive casino bonuses, free spins and no deposit offers updated daily." />
        <meta property="og:url" content="https://playspiro.com/bonuses" />
        <meta property="og:type" content="website" />
      </Helmet>
    <div className="min-h-screen py-8" data-testid="bonuses-page">
      {/* Header */}
      <section className="gradient-hero py-12 mb-8">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-black text-white mb-4 font-['Montserrat']">
            Casino <span className="text-secondary">Bonuses</span>
          </h1>
          <p className="text-white/60 max-w-2xl mx-auto">
            Find the best casino bonuses, welcome offers, and free spins. 
            Exclusive deals you won't find anywhere else.
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4">
        {/* Filters */}
        <div className="glass rounded-2xl p-4 mb-8">
          <div className="flex flex-col sm:flex-row gap-4 items-center">
            <Select
              value={bonusType || ""}
              onValueChange={(value) => setBonusType(value || null)}
            >
              <SelectTrigger className="w-full sm:w-48 bg-white/5 border-white/10 text-white" data-testid="bonus-type-filter">
                <SelectValue placeholder="Bonus Type" />
              </SelectTrigger>
              <SelectContent>
                {bonusTypes.map(type => (
                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button
              variant={exclusiveOnly ? "default" : "outline"}
              onClick={() => setExclusiveOnly(!exclusiveOnly)}
              className={`${exclusiveOnly ? "gradient-gold text-black" : "border-white/20 text-white"} rounded-full`}
              data-testid="exclusive-filter-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Exclusive Only
            </Button>

            {(bonusType || exclusiveOnly) && (
              <Button
                variant="ghost"
                onClick={clearFilters}
                className="text-white/60 hover:text-white"
              >
                <X className="w-4 h-4 mr-1" /> Clear
              </Button>
            )}

            <div className="ml-auto text-white/60 text-sm">
              {filteredBonuses.length} bonuses found
            </div>
          </div>
        </div>

        {/* Top Bonus Banner */}
        {filteredBonuses.length > 0 && (
          <div className="glass rounded-2xl p-6 md:p-8 mb-8 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-primary/20 via-transparent to-secondary/20" />
            <div className="relative flex flex-col md:flex-row items-center gap-6">
              <div className="w-20 h-20 rounded-xl bg-white/10 overflow-hidden border border-white/20">
                <img 
                  src={filteredBonuses[0].casino_logo} 
                  alt={filteredBonuses[0].casino_name}
                  className="w-full h-full object-cover"
                 loading="lazy"/>
              </div>
              <div className="flex-1 text-center md:text-left">
                <div className="flex items-center gap-2 justify-center md:justify-start mb-2">
                  <Gift className="w-5 h-5 text-secondary" />
                  <span className="text-white/60 text-sm uppercase tracking-wider">Today's Top Bonus</span>
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-1">{filteredBonuses[0].casino_name}</h2>
                <p className="text-3xl md:text-4xl font-black text-secondary">{filteredBonuses[0].amount}</p>
                {filteredBonuses[0].free_spins > 0 && (
                  <p className="text-white/60">+ {filteredBonuses[0].free_spins} Free Spins</p>
                )}
              </div>
              <Button className="btn-glow gradient-neon text-white font-bold rounded-full px-8 py-6 text-lg" data-testid="claim-top-bonus-btn">
                Claim Bonus
              </Button>
            </div>
          </div>
        )}

        {/* Inline Ad */}
        <div className="ad-banner h-[90px] rounded-xl mb-8" data-testid="ad-bonuses-inline">
          <span>Ad Space 728x90</span>
        </div>

        {/* Bonus Grid */}
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="h-80 rounded-2xl bg-card animate-pulse" />
            ))}
          </div>
        ) : filteredBonuses.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredBonuses.map((bonus) => (
              <BonusCard key={bonus.id} bonus={bonus} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <Gift className="w-16 h-16 text-white/20 mx-auto mb-4" />
            <p className="text-white/60 text-lg mb-4">No bonuses found matching your criteria.</p>
            <Button onClick={clearFilters} className="gradient-neon text-white rounded-full">
              Clear Filters
            </Button>
          </div>
        )}
      </div>
    </div>
    </>
  );
}
